import styles from "./CheckoutSection.module.css";
import { useState, useEffect, useImperativeHandle, forwardRef } from "react";
import { useNavigate } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { createPaymentIntent } from "../../services/OnlineStore.js";
import useCartStore from "../../states/CartStore";
import { LiaCcVisa, LiaCcMastercard, LiaCcAmex, LiaCcDiscover   } from "react-icons/lia";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || "");

const cardBrands = [
    { name: "Visa", icon: <LiaCcVisa size={24}/> },
    { name: "Mastercard", icon: <LiaCcMastercard size={24} /> },
    { name: "Amex", icon: <LiaCcAmex size={24}/> },
    { name: "Discover", icon: <LiaCcDiscover size={24}/> }
];

const Payment = forwardRef(function Payment({ deliveryInfo, billingInfo, onReadyChange, onProcessingChange, onError }, ref) {
    return (
        <div className={styles.payment}>
            <div className={styles.paymentMethods}>
                <div className={styles.cardBrandRow}>
                    <span className={styles.paymentLabel}>Pay with card</span>
                    <div className={styles.brandList}>
                        {cardBrands.map((brand) => (
                            <span key={brand.name} className={styles.brandChip}>
                                <span className={styles.brandName}>{brand.name}</span>
                                {brand.icon}
                            </span>
                        ))}
                    </div>
                </div>
            </div>

            <Elements stripe={stripePromise}>
                <CreditCardForm
                    ref={ref}
                    deliveryInfo={deliveryInfo}
                    billingInfo={billingInfo}
                    onReadyChange={onReadyChange}
                    onProcessingChange={onProcessingChange}
                    onError={onError}
                />
            </Elements>
        </div>
    );
});

const CreditCardForm = forwardRef(function CreditCardForm({ deliveryInfo, billingInfo, onReadyChange, onProcessingChange, onError }, ref) {
    const navigate = useNavigate();
    const stripe = useStripe();
    const elements = useElements();
    const [error, setError] = useState("");
    const [processing, setProcessing] = useState(false);
    const [cardFocused, setCardFocused] = useState(false);
    const [cardComplete, setCardComplete] = useState(false);
    const cart = useCartStore((state) => state.cart);

    // Let the parent step (Checkout) know when the card is fully entered and Stripe is ready to submit
    useEffect(() => {
        onReadyChange?.(Boolean(stripe && elements) && cardComplete);
    }, [stripe, elements, cardComplete, onReadyChange]);

    useEffect(() => {
        onProcessingChange?.(processing);
    }, [processing, onProcessingChange]);

    // Simple hash function for payment_intent_id
    const hashPaymentId = (paymentIntentId) => {
        return btoa(paymentIntentId).substring(0, 16); // Base64 encode and take first 16 chars
    };

    const handleSubmit = async (event) => {
        event?.preventDefault();

        if (!stripe || !elements) {
            return;
        }

        const cardElement = elements.getElement(CardElement);

        if (!cardElement) {
            onError?.("Card element not ready.");
            return;
        }

        setProcessing(true);
        setError("");
        onError?.(null);

        try {
            const { error: stripeError, paymentMethod } = await stripe.createPaymentMethod({
                type: "card",
                card: cardElement,
            });

            if (stripeError) {
                onError?.(stripeError.message || "Payment failed.");
                return;
            }

            // Create payment intent with backend
            const response = await createPaymentIntent(cart.cart_token, deliveryInfo, billingInfo);
            console.log("Full response from createPaymentIntent:", response);
            console.log("Response keys:", Object.keys(response));
            
            const { payment, customer, order } = response;
            const orderId = order?.id
                ?? order?.ID
                ?? order?.order_id
                ?? response.order_id
                ?? response.orderId
                ?? response.order?.id
                ?? response.order?.ID
                ?? payment?.order_id
                ?? payment?.orderId;
            const clientSecret = payment.client_secret;
            
            console.log("Extracted clientSecret:", clientSecret);
            console.log("Payment data:", payment);
            console.log("Customer data:", customer);
            console.log("Order data:", order);

            // Confirm card payment with Stripe
            const confirmResult = await stripe.confirmCardPayment(clientSecret, {
                payment_method: paymentMethod.id,
            });

            if (confirmResult.error) {
                onError?.(confirmResult.error.message || "Payment failed.");
                return;
            }

            if (confirmResult.paymentIntent.status === "succeeded") {
                // Store response data for success page
                sessionStorage.setItem(
                    "paymentData",
                    JSON.stringify({
                        payment,
                        customer,
                        order,
                        orderId,
                        cartToken: cart.cart_token,
                    })
                );

                // Hash payment intent ID and navigate
                const hashedId = hashPaymentId(payment.payment_intent_id);
                navigate(`/checkout/${hashedId}/success`);
            } else {
                onError?.("Payment did not complete. Please try again.");
            }
        } catch (err) {
            console.error(err);
            onError?.(err.message || "Something went wrong while processing the payment.");
        } finally {
            setProcessing(false);
        }
    };

    // Exposes submit() so the Pay Now action button can live outside the Elements tree
    useImperativeHandle(ref, () => ({
        submit: () => handleSubmit()
    }));

    return (
        <form onSubmit={handleSubmit} className={styles.creditCardForm}>
            <div className={`${styles.cardElementContainer} ${cardFocused ? styles.cardElementFocused : ""} ${error ? styles.cardElementInvalid : ""}`}>
                <CardElement
                    options={{
                        style: {
                            base: {
                                fontSize: "16px",
                                color: "#efebf9",
                                fontFamily: "sans-serif",
                                "::placeholder": {
                                    color: "#9ca3af",
                                },
                            },
                            invalid: {
                                color: "#dc2626",
                                iconColor: "#dc2626",
                            },
                        },
                    }}
                    onFocus={() => setCardFocused(true)}
                    onBlur={() => setCardFocused(false)}
                    onChange={(event) => {
                        setError(event.error ? event.error.message : "");
                        setCardComplete(event.complete);
                    }}
                />
            </div>
            {error && <p className={styles.cardError}>{error}</p>}
        </form>
    );
});

export default Payment;